// API client for our backend (WO-15). All user data (profiles, collections, wishlists, currency
// preferences) lives in our Postgres behind these endpoints; the identity provider (Clerk, or the
// legacy Supabase path behind REACT_APP_AUTH_PROVIDER=supabase) only supplies the session token.

const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin

// The active auth provider registers how to get a fresh session token (Clerk's getToken() or the
// Supabase session). Until then requests go out unauthenticated.
let accessTokenGetter = null

export const setAccessTokenGetter = (getter) => {
  accessTokenGetter = getter
}

export const getAccessToken = async () => {
  if (!accessTokenGetter) return null
  try {
    return (await accessTokenGetter()) || null
  } catch (error) {
    console.error('Error retrieving session token:', error)
    return null
  }
}


const buildAuthHeaders = async (additionalHeaders = {}) => {
  const token = await getAccessToken()
  if (token) {
    return {
      ...additionalHeaders,
      Authorization: `Bearer ${token}`,
    }
  }
  return additionalHeaders
}

export async function fetchAuthBootstrap() {
  try {
    const headers = await buildAuthHeaders({
      'Content-Type': 'application/json'
    })

    const response = await fetch(`${apiBaseUrl}/api/currency/bootstrap`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      return { user: null, profile: null, preferred_currency: null, status: response.status }
    }

    const result = await response.json()
    if (!result?.success || !result?.data) {
      return { user: null, profile: null, preferred_currency: null }
    }

    return {
      user: result.data.user || null,
      profile: result.data.profile || null,
      preferred_currency: result.data.preferred_currency || null
    }
  } catch (_error) {
    return { user: null, profile: null, preferred_currency: null }
  }
}

const jsonOrError = async (response) => {
  let body = null
  try {
    body = await response.json()
  } catch (_error) {
    body = null
  }
  if (response.ok) return { data: body?.data ?? null, error: null }
  const message = body?.detail || body?.message || `Request failed (${response.status})`
  const error = new Error(message)
  error.status = response.status
  if (response.status === 409) error.code = '23505' // keeps the old "duplicate username" handling working
  return { data: null, error }
}

// Profile API functions (our Postgres; replaces direct Supabase `profiles` access)
export const profileAPI = {
  // Own profile + account ({ user, profile })
  getMe: async () => {
    try {
      const headers = await buildAuthHeaders({ 'Content-Type': 'application/json' })
      const response = await fetch(`${apiBaseUrl}/api/profiles/me`, { method: 'GET', headers })
      return await jsonOrError(response)
    } catch (error) {
      return { data: null, error }
    }
  },

  getProfileById: async (userId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/profiles/${encodeURIComponent(userId)}`)
      if (response.status === 404) return { data: null, error: null }
      return await jsonOrError(response)
    } catch (error) {
      return { data: null, error }
    }
  },

  // Public profile by username (case-insensitive). { data: null } when not found.
  getProfile: async (username) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/profiles/by-username/${encodeURIComponent(username || '')}`)
      if (response.status === 404) return { data: null, error: null }
      return await jsonOrError(response)
    } catch (error) {
      return { data: null, error }
    }
  },

  isUsernameAvailable: async (username) => {
    try {
      const headers = await buildAuthHeaders({ 'Content-Type': 'application/json' })
      const params = new URLSearchParams({ username: username || '' })
      const response = await fetch(`${apiBaseUrl}/api/profiles/username-available?${params}`, { headers })
      const { data, error } = await jsonOrError(response)
      return { available: Boolean(data?.available), error }
    } catch (error) {
      return { available: false, error }
    }
  },

  // Update the signed-in user's profile. The server ignores id/email from the client.
  updateMyProfile: async (updates) => {
    try {
      const headers = await buildAuthHeaders({ 'Content-Type': 'application/json' })
      const response = await fetch(`${apiBaseUrl}/api/profiles/me`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates),
      })
      return await jsonOrError(response)
    } catch (error) {
      return { data: null, error }
    }
  },

  // Kept for existing callers: profiles are only ever written by their owner.
  updateProfile: async (_userId, updates) => profileAPI.updateMyProfile(updates),
  createProfile: async (profile) => profileAPI.updateMyProfile(profile),
}

// Public profile lookup by username (or by our user id). Returns the profile or null.
export async function fetchProfileDirect(userId, username) {
  const { data } = userId ? await profileAPI.getProfileById(userId) : await profileAPI.getProfile(username)
  return data || null
}

// Collection API functions
export const collectionAPI = {
  // Get user's collections
  getUserCollections: async (userId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching user collections:', error);
      return { data: [], error };
    }
  },

  // Get another user's public collections + wishlist (no auth needed)
  getPublicProfile: async (userId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/collections/public/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching public profile:', error);
      return { data: null, error };
    }
  },

  // Create a new collection
  createCollection: async (userId, collectionData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections`, {
        method: 'POST',
        headers,
        body: JSON.stringify(collectionData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating collection:', error);
      return { error };
    }
  },

  // Get collection items
  getCollectionItems: async (collectionId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching collection items:', error);
      return { data: null, error };
    }
  },

  // Add item to collection
  addToCollection: async (collectionId, cardId, itemData = {}) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          card_id: cardId,
          quantity: itemData.quantity || 1,
          condition: itemData.condition || 'Near Mint',
          purchase_price: itemData.purchase_price,
          currency: itemData.currency || 'CAD',
          purchase_date: itemData.purchase_date,
          notes: itemData.notes
        })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding to collection:', error);
      return { error };
    }
  },

  // Quick add to default collection
  quickAddToCollection: async (cardId, itemData = {}) => {
    try {
      const params = new URLSearchParams({
        card_id: cardId,
        quantity: itemData.quantity || 1,
        condition: itemData.condition || 'Near Mint'
      });

      if (itemData.purchase_price) {
        params.append('purchase_price', itemData.purchase_price);
      }
      params.append('currency', itemData.currency || 'CAD');

      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/quick-add?${params}`, {
        method: 'POST',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error quick adding to collection:', error);
      return { error };
    }
  },

  // Remove from collection
  removeFromCollection: async (collectionId, cardId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items/${encodeURIComponent(cardId)}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing from collection:', error);
      return { error };
    }
  },

  // Update collection metadata (name, description, is_public)
  updateCollection: async (collectionId, collectionData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(collectionData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating collection:', error);
      return { error };
    }
  },

  // Delete collection
  deleteCollection: async (collectionId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting collection:', error);
      return { error };
    }
  },

  // Update collection item
  updateCollectionItem: async (collectionId, cardId, itemData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items/${encodeURIComponent(cardId)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...itemData,
          currency: itemData.currency || 'CAD'
        })
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating collection item:', error);
      return { error };
    }
  }
}

// Wishlist API functions
export const wishlistAPI = {
  // Get user's wishlist
  getUserWishlist: async (userId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching user wishlist:', error);
      return { data: [], error };
    }
  },

  // Add to wishlist
  addToWishlist: async (cardId, itemData = {}) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          card_id: cardId,
          target_price: itemData.target_price || null,
          currency: itemData.currency || 'CAD',
          notes: itemData.notes
        })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { error };
    }
  },

  // Quick add to wishlist
  quickAddToWishlist: async (cardId, targetPrice = null, currency = 'CAD') => {
    try {
      const params = new URLSearchParams({
        card_id: cardId,
      });
      if (targetPrice != null) {
        params.set('target_price', targetPrice)
      }
      params.set('currency', currency || 'CAD')

      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/quick-add?${params}`, {
        method: 'POST',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error quick adding to wishlist:', error);
      return { error };
    }
  },

  // Remove from wishlist
  removeFromWishlist: async (cardId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/${encodeURIComponent(cardId)}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { error };
    }
  },

  // Update wishlist item
  updateWishlistItem: async (cardId, itemData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/${encodeURIComponent(cardId)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...itemData,
          currency: itemData.currency || 'CAD'
        })
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating wishlist item:', error);
      return { error };
    }
  }
}

export const currencyAPI = {
  getRates: async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/rates`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      return await response.json()
    } catch (error) {
      console.error('Error fetching currency rates:', error)
      return { success: false, data: null, error }
    }
  },

  getPreference: async () => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })
      const response = await fetch(`${apiBaseUrl}/api/currency/preference`, {
        method: 'GET',
        headers
      })
      return await response.json()
    } catch (error) {
      console.error('Error fetching currency preference:', error)
      return { success: false, data: null, error }
    }
  },

  updatePreference: async (preferredCurrency) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })
      const response = await fetch(`${apiBaseUrl}/api/currency/preference`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ preferred_currency: preferredCurrency })
      })
      return await response.json()
    } catch (error) {
      console.error('Error updating currency preference:', error)
      return { success: false, data: null, error }
    }
  }
}


// WO-35: collection / wishlist membership for any product id (Pokemon card ids, other games' cards, sealed).
export const membershipAPI = {
  getStatus: async (ids) => {
    try {
      const headers = await buildAuthHeaders()
      const params = new URLSearchParams({ card_ids: ids.join(',') })
      const response = await fetch(`${apiBaseUrl}/api/collections/status?${params}`, { headers })
      if (!response.ok) return { data: null }
      const result = await response.json()
      return { data: result?.data || null }
    } catch (error) {
      console.error('Error fetching collection status:', error)
      return { data: null, error }
    }
  },
}
